import { useState, useEffect } from 'react';
import { setEntitlementErrorHandler } from '../../services/api';
import UpgradePrompt from './UpgradePrompt';
import ChoosePlanPrompt from './ChoosePlanPrompt';
import SpendingLimitPrompt from './SpendingLimitPrompt';
import { isNoActivePlanError, isSpendingLimitReachedError } from '../../types/billing';
import type { EntitlementError } from '../../types/billing';

/**
 * EntitlementGuard sits at the top of the app tree (inside AppLayout) and
 * wires the global entitlement error handler from the api service layer.
 *
 * When any API call returns 402 limit_exceeded, 402 no_active_plan, or
 * 403 feature_not_entitled, the handler is called and the matching prompt is
 * rendered at the top of the main content area: ChoosePlanPrompt for the
 * no-plan state (the remedy is choosing a plan, task 465), UpgradePrompt for
 * the limit/feature states (the remedy is upgrading), SpendingLimitPrompt for
 * 402 spending_limit_reached (the remedy is the Spending page, task 1159).
 *
 * The prompt can be dismissed by the user. It reappears on the next
 * enforcement error.
 */
export default function EntitlementGuard({ children }: { children: React.ReactNode }) {
  const [entitlementError, setEntitlementError] = useState<EntitlementError | null>(null);

  useEffect(() => {
    setEntitlementErrorHandler((error) => {
      setEntitlementError(error);
    });
    return () => {
      setEntitlementErrorHandler(null);
    };
  }, []);

  return (
    <>
      {entitlementError && (
        <div className="mb-4">
          {isNoActivePlanError(entitlementError) ? (
            <ChoosePlanPrompt onDismiss={() => setEntitlementError(null)} />
          ) : isSpendingLimitReachedError(entitlementError) ? (
            <SpendingLimitPrompt onDismiss={() => setEntitlementError(null)} />
          ) : (
            <UpgradePrompt
              error={entitlementError}
              onDismiss={() => setEntitlementError(null)}
            />
          )}
        </div>
      )}
      {children}
    </>
  );
}
